import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Search,
  Loader2,
  Globe,
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  Server,
  Shield,
  Calendar,
  XCircle,
  ExternalLink,
  Copy,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import Container from '@/components/container';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSearchDomainRdds } from '@/hooks/use-domain-info';
import type { DomainInfo, Contact } from '@/hooks/use-domain-info';
import { cn } from '@/lib/utils';

// ============================================================================
// Helper Components
// ============================================================================

function CopyButton({ value, label }: { value: string; label?: string }) {
  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(value);
    toast.success(`${label || 'Value'} copied`);
  }, [value, label]);

  return (
    <TooltipProvider>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="size-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
            onClick={handleCopy}
          >
            <Copy className="size-3" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">Copy</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function DataRow({ label, value, copyable, className }: { label: string; value: string | null | undefined; copyable?: boolean; className?: string }) {
  if (!value)
    return null;

  return (
    <div className={cn('group flex items-center justify-between gap-2 py-1 text-sm', className)}>
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <div className="flex min-w-0 items-center gap-1">
        <span className="truncate font-medium">{value}</span>
        {copyable && <CopyButton value={value} label={label} />}
      </div>
    </div>
  );
}

function StatusBadge({ status, colorCode }: { status: string; colorCode?: string }) {
  const getStatusStyle = () => {
    const s = status.toLowerCase();
    if (s === 'registered')
      return 'bg-green-100 text-green-800';
    if (s === 'pending' || s === 'pendingcreate')
      return 'bg-amber-100 text-amber-800';
    if (s === 'redemption')
      return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getColorDot = () => {
    if (!colorCode)
      return null;
    switch (colorCode) {
      case 'blue': {
        return 'bg-blue-500';
      }
      case 'green': {
        return 'bg-green-500';
      }
      case 'goldenrod': {
        return 'bg-amber-500';
      }
      case 'red': {
        return 'bg-red-500';
      }
      case 'purple': {
        return 'bg-purple-500';
      }
      default: {
        return null;
      }
    }
  };

  const dotColor = getColorDot();

  return (
    <div className="flex items-center gap-2">
      {dotColor && (
        <TooltipProvider>
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <div className={cn('size-2.5 rounded-full', dotColor)} />
            </TooltipTrigger>
            <TooltipContent side="top">{colorCode}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <Badge className={getStatusStyle()}>{status}</Badge>
    </div>
  );
}

function EppStatusList({ statuses }: { statuses: string[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {statuses.map((status) => {
        const isHold = status.toLowerCase().includes('hold');
        const isProhibited = status.toLowerCase().includes('prohibited');
        return (
          <Badge
            key={status}
            variant="outline"
            className={cn(
              'text-xs',
              isHold && 'border-red-300 text-red-700',
              isProhibited && !isHold && 'border-amber-300 text-amber-700',
            )}
          >
            {status}
          </Badge>
        );
      })}
    </div>
  );
}

function ContactRow({ contact, type }: { contact: Contact; type: string }) {
  const formatAddress = (addr: Contact['Address']) => {
    return [
      ...(addr.Street?.filter(Boolean) || []),
      addr.City,
      addr['State/Province'],
      addr['Postal Code'],
      addr.Country,
    ].filter(Boolean).join(', ');
  };

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{type}</span>
            <span className="text-xs text-muted-foreground">
              ID:
              {contact.ID}
            </span>
          </div>
          <div className="group flex items-center gap-2">
            <User className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate font-medium">{contact.Name}</span>
            <CopyButton value={contact.Name} label="Name" />
          </div>
          {contact.Organisation && (
            <div className="group flex items-center gap-2 text-sm">
              <Building2 className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{contact.Organisation}</span>
              <CopyButton value={contact.Organisation} label="Organization" />
            </div>
          )}
          <div className="group flex items-center gap-2 text-sm">
            <Mail className="size-3.5 shrink-0 text-muted-foreground" />
            <a href={`mailto:${contact.Email}`} className="truncate text-primary hover:underline">
              {contact.Email}
            </a>
            <CopyButton value={contact.Email} label="Email" />
          </div>
          {contact.Phone?.Number && (
            <div className="group flex items-center gap-2 text-sm">
              <Phone className="size-3.5 shrink-0 text-muted-foreground" />
              <span>{contact.Phone.Number}</span>
              <CopyButton value={contact.Phone.Number} label="Phone" />
            </div>
          )}
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <MapPin className="mt-0.5 size-3.5 shrink-0" />
            <span className="truncate">{formatAddress(contact.Address)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function NameServerRow({ ns }: { ns: DomainInfo['Name Servers'][0] }) {
  return (
    <div className="py-2 first:pt-0 last:pb-0">
      <div className="text-sm font-medium">{ns['Name Server Name']}</div>
      <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {ns['IPv4 Addresses']?.length > 0 && (
          <span>
            IPv4:
            {ns['IPv4 Addresses'].join(', ')}
          </span>
        )}
        {ns['IPv6 Addresses']?.length > 0 && (
          <span>
            IPv6:
            {ns['IPv6 Addresses'].join(', ')}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function RddsSearchPage() {
  const [searchInput, setSearchInput] = useState('');
  const [searchedDomain, setSearchedDomain] = useState<string | null>(null);
  const { setLabel, removeLabel } = useBreadcrumb();

  const { mutate: searchRdds, data: domainInfo, isPending, isError, error, reset } = useSearchDomainRdds();

  useEffect(() => {
    const path = '/app/monitor/rdds-search';
    setLabel(path, { name: 'RDDS Search', path });
    return () => removeLabel(path);
  }, [setLabel, removeLabel]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const domain = searchInput.trim().toLowerCase();
    if (!domain) {
      toast.error('Please enter a domain name');
      return;
    }
    if (!domain.includes('.')) {
      toast.error('Please enter a valid domain name (e.g., example.bank)');
      return;
    }
    setSearchedDomain(domain);
    reset();
    searchRdds(domain, {
      onError: (err) => {
        toast.error(err.message || 'Failed to fetch RDDS data');
      },
    });
  }, [searchInput, searchRdds, reset]);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  const hasContacts = domainInfo && (
    domainInfo['Registrant Contact']
    || domainInfo['Admin Contacts']?.length
    || domainInfo['Technical Contacts']?.length
    || domainInfo['Billing Contacts']?.length
  );

  const hasDnssec = domainInfo?.['DNSSEC Information']?.['DS Data items']?.length > 0;

  return (
    <Container className="pb-12">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">RDDS Search</h1>
          <p className="text-sm text-muted-foreground">
            Real-time RDDS lookup for .Bank and .Insurance domains
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/app/monitor">Back to Monitor</Link>
        </Button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6 flex gap-2">
        <div className="relative flex-1">
          <Globe className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Enter domain name (e.g., example.bank)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button type="submit" disabled={isPending}>
          {isPending
            ? (
                <Loader2 className="size-4 animate-spin" />
              )
            : (
                <Search className="size-4" />
              )}
          <span className="ml-2">Search</span>
        </Button>
      </form>

      {/* Error State */}
      {isError && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <XCircle className="size-5 shrink-0 text-red-600" />
              <div>
                <div className="font-medium text-red-800">Domain Not Found</div>
                <p className="text-sm text-red-700">
                  {error?.message || `Unable to find RDDS data for "${searchedDomain}".`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isPending && (
        <Card className="mb-6">
          <CardContent className="flex items-center justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">
              Fetching RDDS data for
              {searchedDomain}
              ...
            </span>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {domainInfo && !isPending && (
        <div className="space-y-4">
          {/* Domain Header */}
          <div className="flex items-start justify-between gap-4 rounded-lg border bg-card p-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-lg font-semibold">{domainInfo['Domain Name']['User form']}</h2>
                <StatusBadge status={domainInfo['Registry Status']} colorCode={domainInfo.colorCode} />
                <a
                  href={`https://${domainInfo['Domain Name']['User form']}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ExternalLink className="size-4" />
                </a>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                ROID:
                {domainInfo.ROID}
              </div>
              <div className="mt-3">
                <EppStatusList statuses={domainInfo['EPP Statuses']} />
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Dates & Registrar */}
              <div className="rounded-lg border bg-card p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                  <Calendar className="size-4 text-muted-foreground" />
                  Dates & Registrar
                </div>
                <div className="space-y-0.5">
                  <DataRow label="Created" value={formatDate(domainInfo['Creation Date'])} />
                  <DataRow label="Expires" value={formatDate(domainInfo['Expiry Date'])} />
                  <DataRow label="Last Updated" value={formatDate(domainInfo['Last Updated Date'])} />
                  <Separator className="my-2" />
                  <DataRow label="Registrar" value={domainInfo['Sponsoring Registrar']} />
                  <DataRow label="IANA ID" value={domainInfo['Sponsoring Registrar IANA ID']} />
                </div>
              </div>

              {/* Name Servers */}
              {domainInfo['Name Servers']?.length > 0 && (
                <div className="rounded-lg border bg-card p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                    <Server className="size-4 text-muted-foreground" />
                    Name Servers
                    <Badge variant="outline" className="ml-auto">{domainInfo['Name Servers'].length}</Badge>
                  </div>
                  <div className="divide-y">
                    {domainInfo['Name Servers'].map((ns, idx) => (
                      <NameServerRow key={ns.ROID || idx} ns={ns} />
                    ))}
                  </div>
                </div>
              )}

              {/* DNSSEC */}
              <div className="rounded-lg border bg-card p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                  <Shield className="size-4 text-muted-foreground" />
                  DNSSEC
                  {hasDnssec
                    ? (
                        <Badge className="ml-auto bg-green-100 text-green-800">Enabled</Badge>
                      )
                    : (
                        <Badge variant="outline" className="ml-auto">Not Configured</Badge>
                      )}
                </div>
                {hasDnssec && (
                  <div className="space-y-2">
                    {domainInfo['DNSSEC Information']['DS Data items'].map((item, idx) => (
                      <div key={idx} className="space-y-1 text-xs">
                        <div className="font-medium">{item['DS Data Name']}</div>
                        {item['DS Data Elements']?.map((elem, elemIdx) => (
                          <div key={elemIdx} className="border-l-2 pl-3 text-muted-foreground">
                            <div>
                              Algorithm:
                              {elem.Algorithm}
                              {' '}
                              • Key Tag:
                              {elem['Key Tag']}
                              {' '}
                              • Digest Type:
                              {elem['Digest Type']}
                            </div>
                            <div className="break-all font-mono text-[10px]">{elem.Digest}</div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Contacts */}
            {hasContacts && (
              <div className="rounded-lg border bg-card p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                  <User className="size-4 text-muted-foreground" />
                  Contacts
                </div>
                <div className="divide-y">
                  {domainInfo['Registrant Contact'] && (
                    <ContactRow contact={domainInfo['Registrant Contact']} type="Registrant" />
                  )}
                  {domainInfo['Admin Contacts']?.map((c, idx) => (
                    <ContactRow key={c.ROID || idx} contact={c} type="Admin" />
                  ))}
                  {domainInfo['Technical Contacts']?.map((c, idx) => (
                    <ContactRow key={c.ROID || idx} contact={c} type="Technical" />
                  ))}
                  {domainInfo['Billing Contacts']?.map((c, idx) => (
                    <ContactRow key={c.ROID || idx} contact={c} type="Billing" />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* View in Domain Details */}
          <div className="flex justify-center pt-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/app/domains/${domainInfo['Domain Name']['User form']}`}>
                View Full Domain Details
                <ChevronRight className="ml-1 size-4" />
              </Link>
            </Button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!domainInfo && !isPending && !isError && (
        <div className="py-12 text-center">
          <Globe className="mx-auto size-10 text-muted-foreground/30" />
          <h3 className="mt-3 text-sm font-medium">Search for a Domain</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
            Enter a .Bank or .Insurance domain to retrieve its RDDS data from the registry.
          </p>
        </div>
      )}
    </Container>
  );
}

export default RddsSearchPage;
